import type {
  CookingSession,
  CookingSessionStatus,
  ListCookingSessionsParams,
} from "@/types/cooking-session";
import { isRunning, tickedSeconds, type ElapsedSource } from "@/lib/elapsed";

/**
 * Multi View (YC-12): the logic that decides what a chip, a card or a top bar
 * says about a cook. Pure, so the views only lay it out and the rules (what
 * counts as late, when Next stage shows, which recipe takes the spotlight) are
 * tested without a browser.
 */

/** The dot on a chip: blue cooking, red late, grey on deck. Paused is not in the design; it reads amber. */
export type SessionTone = "cooking" | "late" | "onDeck" | "paused";

/** What Multi View can hold: recipes still in the kitchen, whether or not started. */
export const MULTI_VIEW_STATUSES: CookingSessionStatus[] = ["ON_DECK", "COOKING", "PAUSED"];

/** The list endpoint pages at 100, which is the most one screen is asked to hold. */
export const MULTI_VIEW_LIMIT = 100;

/**
 * The one query behind the chips on Prep and the screen itself, so the two
 * share a cache entry and agree on what a recipe's status is.
 */
export function multiViewSessionsParams(locationId: number | undefined): ListCookingSessionsParams {
  return {
    locationId,
    source: "prep",
    status: MULTI_VIEW_STATUSES,
    limit: MULTI_VIEW_LIMIT,
    page: 1,
  };
}

/**
 * Late is the server's word (`isLate`) or, between polls, the running clock
 * passing the target - the same line the board's progress ring turns red at.
 * A recipe that has not started cannot be late.
 */
export function isSessionLate(session: CookingSession, liveSeconds: number): boolean {
  if (session.status === "ON_DECK") return false;
  if (session.status === "COMPLETED" || session.status === "CANCELLED") return false;
  return session.isLate || (isRunning(session.status) && liveSeconds > session.targetMinutes * 60);
}

/** Whole minutes past the target, for "Late - +20m over". */
export function minutesOver(session: Pick<CookingSession, "targetMinutes">, liveSeconds: number): number {
  return Math.max(0, Math.floor((liveSeconds - session.targetMinutes * 60) / 60));
}

/** Whole minutes left of the target (floored: 15:58 of 45m is "29m left"). */
export function minutesLeft(session: Pick<CookingSession, "targetMinutes">, liveSeconds: number): number {
  return Math.max(0, Math.floor((session.targetMinutes * 60 - liveSeconds) / 60));
}

export function sessionTone(session: CookingSession, liveSeconds: number): SessionTone {
  if (session.status === "ON_DECK") return "onDeck";
  if (isSessionLate(session, liveSeconds)) return "late";
  if (session.status === "PAUSED") return "paused";
  return "cooking";
}

/** Started time as the board writes it (hh:mm), or a dash before the cook begins. */
export function startedLabel(session: Pick<CookingSession, "startedAt">): string {
  return session.startedAt
    ? new Date(session.startedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "—";
}

/** Seconds on a session's clock now, given when the figure was fetched. */
export function liveSecondsAt(session: ElapsedSource, fetchedAtMs: number, nowMs: number): number {
  return tickedSeconds(session.elapsedSeconds, fetchedAtMs, nowMs, isRunning(session.status));
}

/** Stages in the recipe: the API repeats it on the current stage; the recipe's own list is the fallback. */
export function stageCountOf(session: CookingSession, recipeStageCount: number | undefined): number {
  return session.currentStage?.total ?? recipeStageCount ?? 0;
}

/**
 * Next stage shows only on a cook in progress whose recipe has stages and has
 * a next one (product, 3 Nov). A cook with no stage started yet moves into the
 * first; on the last stage there is nothing to move to, so it hides and Mark
 * done is what is left.
 */
export function canMoveToNextStage(session: CookingSession, recipeStageCount: number | undefined): boolean {
  if (session.status !== "COOKING") return false;
  const total = stageCountOf(session, recipeStageCount);
  if (total <= 0) return false;
  const current = session.currentStage;
  if (current) return current.position + 1 < total;
  // No current stage: not started yet, or every stage already finished.
  return (session.stages ?? []).length === 0;
}

/** NOW for the stage being cooked, DONE for the ones behind it, nothing ahead. */
export type StageMark = "now" | "done" | "upcoming";

export function stageMark(session: CookingSession, position: number): StageMark {
  const current = session.currentStage;
  if (current && current.position === position) return "now";
  const reached = (session.stages ?? []).find((s) => s.position === position);
  if (reached?.finishedAt) return "done";
  return "upcoming";
}

export interface ViewCounts {
  open: number;
  late: number;
}

/** "4 open - 1 late" for the Grid header. */
export function countView(
  sessions: CookingSession[],
  liveSecondsOf: (s: CookingSession) => number,
): ViewCounts {
  return {
    open: sessions.length,
    late: sessions.filter((s) => isSessionLate(s, liveSecondsOf(s))).length,
  };
}

/** The sessions in the view, in the order they were ticked (ids), dropping any that left the board. */
export function resolveView(sessions: CookingSession[], ids: number[]): CookingSession[] {
  const byId = new Map(sessions.map((s) => [s.id, s]));
  return ids.flatMap((id) => {
    const found = byId.get(id);
    return found ? [found] : [];
  });
}

/**
 * Which recipe holds the spotlight.
 *
 * It keeps whatever the cook picked, unless that left the view, in which case
 * the first recipe (a late one if there is one) takes over. With auto-focus on,
 * a recipe that has just GONE late takes it instead: only a change of state
 * moves the spotlight, so a recipe that stays late does not fight the cook's
 * own pick on every poll.
 */
export function nextSpotlight(args: {
  current: number | null;
  ids: number[];
  lateIds: number[];
  previousLateIds: number[];
  autoFocusLate: boolean;
}): number | null {
  const { current, ids, lateIds, previousLateIds, autoFocusLate } = args;
  if (ids.length === 0) return null;
  if (autoFocusLate) {
    const newlyLate = ids.find((id) => lateIds.includes(id) && !previousLateIds.includes(id));
    if (newlyLate !== undefined) return newlyLate;
  }
  if (current !== null && ids.includes(current)) return current;
  return ids.find((id) => lateIds.includes(id)) ?? ids[0] ?? null;
}

/** Previous/next tab, wrapping nowhere: the arrows disable at either end. */
export function neighbourTab(ids: number[], current: number | null, step: -1 | 1): number | null {
  if (current === null) return ids[0] ?? null;
  const next = ids.indexOf(current) + step;
  return next >= 0 && next < ids.length ? (ids[next] ?? current) : current;
}
